import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Link as RouterLink,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";

import { BrandMetadata_T } from "../../types/Brand";
import { TriggerBrandRefreshButton } from "../../components/Brand/UI/TriggerBrandRefreshButton";
import { Box, Breadcrumbs, Tab, Tabs, Typography } from "@mui/material";

import BrandCountCountryTable from "../../components/Brand/Reports/BrandCountByCountryTable";
import BrandCountBySourceTable from "../../components/Brand/Reports/BrandCountBySourceTable";
import BrandFillRatesByCountryTable from "../../components/Brand/Reports/BrandFillRatesByCountryTable";
import BrandFillRatesBySourceTable from "../../components/Brand/Reports/BrandFillRatesBySourceTable";
import ChildrenBrandsbyBrandsTable from "../../components/Brand/Reports/ChildrenBrandsbyBrandTable";
import ParentBrandsbyBrandsTable from "../../components/Brand/Reports/ParentBrandsbyBrandTable";
import BrandMetadataReview from "../../components/Brand/Tasks/BrandMetadataReview";
import BrandMetadataReviewByCountry from "../../components/Brand/Tasks/BrandMetadataReviewByCountry";
import PlacesReviewNonOmni from "../../components/Brand/Tasks/PlacesReviewNonOmni";
import PlacesReviewOmniscrapers from "../../components/Brand/Tasks/PlacesReviewOmniscrapers";
import PlacesReviewGeneral from "../../components/Brand/Tasks/PlacesReviewGeneral";
import PlacesReviewLocationParent from "../../components/Brand/Tasks/PlacesReviewLocationParent";

import { formatDateHumanReadable } from "utils/formatting";

const DataOpsBrandDetailPage: React.FC = () => {
  const { showError } = useError();
  const { apiRequest } = useApi();

  const { brand_id } = useParams<{ brand_id: string }>();
  const location = useLocation();

  const [isLogoImageBroken, setIsLogoImageBroken] = useState(false);
  const [brandMetadata, setBrandMetadata] = useState<BrandMetadata_T>({
    brand_name: "",
  });
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedTabName, setSelectedTabName] = useState<string>("");
  const [tabContainerRef, setTabContainerRef] = useState<HTMLDivElement | null>(
    null
  );

  document.title = `Brand: ${brandMetadata.brand_name} - ${selectedTabName}`;

  const tabMapping = [
    { path: "count-by-country", name: "Count by Country" },
    { path: "count-by-source", name: "Count by Source" },
    { path: "fill-rates", name: "Fill Rates" },
    { path: "fill-rates-by-source", name: "Fill Rates by Source" },
    { path: "parent-brands", name: "Parent Brands" },
    { path: "children-brands", name: "Children Brands" },
    { path: "metadata-review", name: "Metadata Review" },
    { path: "metadata-review-country", name: "Metadata Review by Country" },
    { path: "places-review-omni", name: "Places Review by Omni" },
    { path: "nonomni-places-review", name: "Non-Omni Places Review" },
    { path: "general-places-review", name: "General Places Review" },
    { path: "location-parent-review", name: "Location Parent Reivew" },
  ];

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const matchedTab = tabMapping.findIndex((tab) => tab.path === currentPath);
    if (matchedTab !== -1) {
      setSelectedTab(matchedTab);
      setSelectedTabName(tabMapping[matchedTab].name);
    }
  }, [location.pathname]);

  useEffect(() => {
    setMetadataLoading(true);

    if (brand_id) {
      const fetchBrandMetadata = async () => {
        try {
          const response = await apiRequest<BrandMetadata_T>(
            `/brand_reports/brand_metadata/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setBrandMetadata(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(`Failed to fetch brand metadata for ${brand_id}.`);
        } finally {
          setMetadataLoading(false);
        }
      };

      fetchBrandMetadata();
    } else {
      console.warn("No brand_id provided");
      setMetadataLoading(false);
    }
  }, [brand_id]);

  const scrollTabs = (direction: "left" | "right") => {
    if (tabContainerRef) {
      const scrollAmount = direction === "left" ? -200 : 200;
      tabContainerRef.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <>
      {metadataLoading ? (
        <div>Loading...</div>
      ) : (
        <Box>
          <h1>Brand: {brandMetadata.brand_name}</h1>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <RouterLink
              to="/data-ops/brands/summary"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              Brands
            </RouterLink>
            <Typography color="textPrimary">
              {brandMetadata.brand_name}
            </Typography>
          </Breadcrumbs>
          <div style={{ marginBottom: "20px" }}>
            <div className="txt-pad-b-5">
              {!isLogoImageBroken && brandMetadata.logo ? (
                <img
                  src={brandMetadata.logo}
                  alt="Brand Logo"
                  style={{
                    maxWidth: "150px",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                  onError={() => setIsLogoImageBroken(true)}
                />
              ) : (
                ""
              )}
            </div>
            <div className="txt-pad-b-5">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>
                  <strong>Chain ID:</strong> {brandMetadata.chain_id}
                </Typography>
                {brandMetadata.chain_id && (
                  <TriggerBrandRefreshButton chainId={brandMetadata.chain_id} />
                )}
              </Box>
            </div>

            <div className="txt-pad-b-5">
              <strong>Stock Ticker ID:</strong>{" "}
              {brandMetadata.stock_ticker_id || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Wikipedia:</strong>{" "}
              {brandMetadata.wikipedia ? (
                <a
                  href={brandMetadata.wikipedia}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {brandMetadata.wikipedia}
                </a>
              ) : (
                "N/A"
              )}
            </div>
            <div className="txt-pad-b-5">
              <strong>All Names:</strong> {brandMetadata.all_names || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Website Domains:</strong>{" "}
              {brandMetadata.website_domains || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Primary Website:</strong>{" "}
              {brandMetadata.website ? (
                <a
                  href={brandMetadata.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {brandMetadata.website}
                </a>
              ) : (
                "N/A"
              )}
            </div>
            <div className="txt-pad-b-5">
              <strong>Business Categories:</strong>{" "}
              {brandMetadata.business_categories || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Child Business Categories:</strong>{" "}
              {brandMetadata.child_business_categories || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Ownership Parent ID:</strong>{" "}
              {brandMetadata.ownership_parent_id || "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Frozen At:</strong>{" "}
              {brandMetadata.frozen_at
                ? formatDateHumanReadable(new Date(brandMetadata.frozen_at))
                : "N/A"}
            </div>
            <div className="txt-pad-b-5">
              <strong>Top Listed Classification:</strong>{" "}
              {brandMetadata.top_listed_classification || "N/A"}
            </div>
          </div>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                flexGrow: 1,
                overflowX: "auto",
                whiteSpace: "nowrap",
                scrollbarWidth: "thin",
              }}
              ref={setTabContainerRef}
            >
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="scrollable tabs"
              >
                {tabMapping.map((tab, index) => (
                  <Tab
                    key={tab.path}
                    label={tab.name}
                    component={RouterLink}
                    to={`/data-ops/brand/${brand_id}/${tab.path}`}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>
          <Routes>
            <Route
              path="/"
              element={
                <Navigate
                  to={`/data-ops/brand/${brand_id}/count-by-country`}
                  replace={true}
                />
              }
            />
            <Route
              path="count-by-country"
              element={<BrandCountCountryTable brand_id={brand_id || ""} />}
            />
            <Route
              path="count-by-source"
              element={<BrandCountBySourceTable brand_id={brand_id || ""} />}
            />
            <Route
              path="fill-rates"
              element={
                <BrandFillRatesByCountryTable brand_id={brand_id || ""} />
              }
            />
            <Route
              path="fill-rates-by-source"
              element={
                <BrandFillRatesBySourceTable brand_id={brand_id || ""} />
              }
            />
            <Route
              path="parent-brands"
              element={<ParentBrandsbyBrandsTable brand_id={brand_id || ""} />}
            />
            <Route
              path="children-brands"
              element={
                <ChildrenBrandsbyBrandsTable brand_id={brand_id || ""} />
              }
            />
            <Route
              path="metadata-review"
              element={
                <BrandMetadataReview
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                  chain_id={brandMetadata.chain_id || ""}
                />
              }
            />
            <Route
              path="metadata-review-country"
              element={
                <BrandMetadataReviewByCountry
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                  chain_id={brandMetadata.chain_id || ""}
                />
              }
            />
            <Route
              path="places-review-omni"
              element={
                <PlacesReviewOmniscrapers
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                  chain_id={brandMetadata.chain_id || ""}
                />
              }
            />
            <Route
              path="nonomni-places-review"
              element={
                <PlacesReviewNonOmni
                  chain_id={brandMetadata.chain_id || ""}
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                />
              }
            />
            <Route
              path="general-places-review"
              element={
                <PlacesReviewGeneral
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                />
              }
            />
            <Route
              path="location-parent-review"
              element={
                <PlacesReviewLocationParent
                  chain_id={brandMetadata.chain_id || ""}
                  brand_id={brand_id || ""}
                  brand_name={brandMetadata.brand_name || ""}
                />
              }
            />
          </Routes>
        </Box>
      )}
    </>
  );
};

export default DataOpsBrandDetailPage;
